import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    inject,
    input,
    model,
    OnDestroy,
    OnInit,
    output,
    signal,
    Signal,
    TemplateRef,
    ViewEncapsulation,
} from '@angular/core';
import {
    ICurrentOpenedCompletionStepData,
    IOnboardingCompletion,
    IOnboardingCompletionSaveModel,
    IOnboardingCompletionSection,
    IOnboardingCompletionStep,
    IOnboardingCompletionUserStep,
    IOnboardingQuizCompletionTimerData,
    IOnboardingUserCompletion,
} from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { FiOnboardingCompletionStateType } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-enums';
import { flattenSimple } from '@fi-shared/utils/flatten-simple';
import { FiOnboardingCompletionHelperService } from '../fi-onboarding-completion-helper.service';
import { cloneDeep, isDefined, isEqual } from '@fi-shared/utils';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { Subject } from 'rxjs';
import { FiOnboardingCompletionSyncService } from '../fi-onboarding-completion-sync.service';
import { Animations } from '@fi-shared/utils/animations';
import { IOnboardingCompletionChangedEventItem } from '@fi-shared/fi-signal-r/fi-signal-r';
import { FiOnboardingActiveDataService } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-data.service';
import { FiOnboardingQuizCompletionStateType } from '../fi-onboarding-completion-enums';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { FiOnboardingHelperService } from '@fi-app/fi-onboarding/fi-onboarding-helper.service';
import { FiOnboardingCompletionService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion.service';

@Component({
    selector: 'fi-onboarding-completion',
    templateUrl: './fi-onboarding-completion.component.html',
    styleUrl: './fi-onboarding-completion.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-onboarding-completion' },
    standalone: false,
    animations: [Animations.fadeHeight()],
})
export class FiOnboardingCompletionComponent implements OnInit, OnDestroy {
    public onboardingCompletion = input.required<IOnboardingCompletion>();
    public isOnboardingNameDisplayed = input<boolean>(true);
    public userCompletion = model.required<IOnboardingUserCompletion>();
    public maxDescriptionLines = input<number>(2);
    public expandOnCardClick = input<boolean>(false);
    public isHomePage = input<boolean>(false);
    public onDialogOpened = output<void>();

    protected userCompletionStepById = computed<Map<number, IOnboardingCompletionUserStep>>(() => {
        return new Map(this.userCompletion().Steps.map((step) => [step.OnboardingStepId, step]));
    });

    public onboardingId = input.required<number>();

    public isFoldable = input<boolean>(false);
    public isBackButtonVisible = input<boolean>(false);
    public isHeaderVisible = input<boolean>(true);
    public actionsTemplate = input<TemplateRef<any>>(null);

    public backButtonClick = output<void>();
    private readonly fiOnboardingCompletionHelperService = inject(FiOnboardingCompletionHelperService);
    private readonly fiOnboardingCompletionSyncService = inject(FiOnboardingCompletionSyncService);
    private readonly fiOnboardingActiveDataService = inject(FiOnboardingActiveDataService);
    private readonly fiOnboardingHelperService = inject(FiOnboardingHelperService);
    private readonly fiOnboardingCompletionService = inject(FiOnboardingCompletionService);

    protected readonly isStepCompletedFn = (step: IOnboardingCompletionStep, index: number): boolean =>
        this.isStepCompleted(step, index);
    protected readonly isStepActiveFn = (step: IOnboardingCompletionStep, _index: number): boolean =>
        this.isActiveStep(step);
    protected readonly isStepDisabledFn = (step: IOnboardingCompletionStep, index: number): boolean =>
        this.isStepDisabled(step, index);
    protected readonly isStepInProgressFn = (step: IOnboardingCompletionStep, index: number): boolean =>
        this.isStepInProgress(step, index);
    protected readonly getTimerDataFn = (
        step: IOnboardingCompletionStep,
        index: number,
    ): IOnboardingQuizCompletionTimerData | undefined => this.getTimerData(step, index);

    protected isExpanded = signal(true);

    protected visibleSections = computed<IOnboardingCompletionSection[]>(() =>
        this.onboardingCompletion().Sections.filter((section) => !section.IsDisabled),
    );
    private visibleSteps = computed<IOnboardingCompletionStep[]>(() =>
        flattenSimple(this.visibleSections().map((section) => section.Steps)),
    );
    protected allSteps = computed<IOnboardingCompletionStep[]>(() =>
        flattenSimple(this.onboardingCompletion().Sections.map((section) => section.Steps)),
    );
    protected userSteps = computed<IOnboardingCompletionUserStep[]>(() =>
        this.allSteps()?.map((step) => this.userCompletionStepById().get(step.OnboardingStepId)),
    );
    protected availableStepsCount = computed<number>(
        () => this.visibleSteps()?.filter((step) => !step.IsDisabled).length,
    );
    protected userActiveStep = computed<IOnboardingCompletionStep | undefined>(() => {
        return this.visibleSteps()?.find((step) => {
            const userStep = this.userCompletionStepById().get(step.OnboardingStepId);
            return (
                !step.IsDisabled &&
                (userStep.CompletionState === FiOnboardingCompletionStateType.NotCompleted ||
                    userStep.CompletionState === FiOnboardingCompletionStateType.InProgress)
            );
        });
    });

    protected userActiveStepIndex = computed<number>(() => {
        return this.allSteps()?.indexOf(this.userActiveStep());
    });

    protected activeSection = computed<IOnboardingCompletionSection | undefined>(() => {
        return this.onboardingCompletion().Sections.find((section) =>
            section.Steps.find((step) => isEqual(step, this.userActiveStep())),
        );
    });

    protected completedSections = computed<IOnboardingCompletionSection[]>(() => {
        if (!isDefined(this.userActiveStep())) {
            return this.onboardingCompletion().Sections;
        }
        return this.onboardingCompletion().Sections.filter((section) =>
            section.Steps.filter((step) => !step.IsDisabled).every(
                (step) =>
                    this.userCompletionStepById().get(step.OnboardingStepId).CompletionState ===
                    FiOnboardingCompletionStateType.Completed,
            ),
        );
    });
    protected completedSteps = computed<IOnboardingCompletionUserStep[]>(() =>
        this.visibleSteps()
            .filter((step) => !step.IsDisabled)
            .map((step) => this.userCompletionStepById().get(step.OnboardingStepId))
            .filter((step) => step.CompletionState === FiOnboardingCompletionStateType.Completed),
    );

    private currentOpenedCompletionStep: Signal<ICurrentOpenedCompletionStepData | undefined> =
        this.fiOnboardingCompletionSyncService.currentOpenedCompletionStepData;

    private readonly onboardingActiveDataEffectRef = effect(() => {
        const completedSteps = this.completedSteps();
        this.fiOnboardingActiveDataService.reloadOnboardingActiveData(); // todo not optimal
    });

    public ngOnInit(): void {
        this.subscribeOnCompletionChangedEvent();
        if (!isNullOrUndefined(this.currentOpenedCompletionStep())) {
            this.isExpanded.set(true);
            this.openStepCompletionDialog();
            this.onDialogOpened.emit();
        }
    }

    protected toggleContentExpansionState(): void {
        this.isExpanded.set(!this.isExpanded());
    }

    private subscribeOnCompletionChangedEvent(): void {
        this.fiOnboardingCompletionSyncService.subscribeToOnboardingCompletionChanged({
            onboardingId: this.onboardingId(),
            userId: this.userCompletion().UserId,
            userCompletionSteps: this.userCompletion().Steps,
            onDestroy$: this.ngOnDestroy$,
            callback: (updatedCompletions) => {
                this.updateUserCompletionFromSync(updatedCompletions);
            },
        });
    }

    private updateUserCompletionFromSync(
        updatedCompletions: { completionChangedEventItem: IOnboardingCompletionChangedEventItem }[],
    ): void {
        const completionByStepId = new Map(
            updatedCompletions.map((item) => [
                item.completionChangedEventItem.OnboardingStepId,
                item.completionChangedEventItem,
            ]),
        );

        this.userCompletion.update((userCompletion) => ({
            ...userCompletion,
            Steps: userCompletion.Steps.map((step) => {
                const completionChangedEventItem = completionByStepId.get(step.OnboardingStepId);
                if (isNullOrUndefined(completionChangedEventItem)) {
                    return step;
                }

                return {
                    ...step,
                    CompletionState: completionChangedEventItem.OnboardingStepCompletionState,
                    QuizCompletion: step.QuizCompletion
                        ? {
                              ...step.QuizCompletion,
                              CompletionState: completionChangedEventItem.QuizCompletionState,
                          }
                        : step.QuizCompletion,
                };
            }),
        }));
    }

    protected getUserCompletionProgressPercentage(section: IOnboardingCompletionSection): number {
        const sectionSteps = section.Steps.filter((step) => !step.IsDisabled).map((step) =>
            this.userCompletionStepById().get(step.OnboardingStepId),
        );
        const completedSteps = sectionSteps.filter(
            (step) => step.CompletionState === FiOnboardingCompletionStateType.Completed,
        );
        if (sectionSteps.length === 0) {
            return 0;
        }
        return Math.round((completedSteps.length / sectionSteps.length) * 100);
    }

    private isActiveStep(step: IOnboardingCompletionStep): boolean {
        return isEqual(step, this.userActiveStep());
    }

    protected onOpenStepClick(completionStep: IOnboardingCompletionStep) {
        if (this.userCompletionStepById().get(completionStep.OnboardingStepId).HasViewAccess) {
            completionStep = this.handleQuizTimerSettingsChangedAfterStart(completionStep);

            this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData({
                onboardingCompletionStep: completionStep,
                onboardingId: this.onboardingId(),
                stepId: completionStep.OnboardingStepId,
                isHomePage: this.isHomePage(),
            });

            this.openStepCompletionDialog();
            return;
        }
        this.fiOnboardingCompletionHelperService.showAccessErrorDialog(completionStep.ObjectType);
    }

    private handleQuizTimerSettingsChangedAfterStart(completionStep: IOnboardingCompletionStep) {
        if (completionStep.ObjectType !== ObjectTypes.Quiz) {
            return completionStep;
        }

        const userCompletionStep = this.userCompletionStepById().get(completionStep.OnboardingStepId);
        if (
            userCompletionStep.CompletionState === FiOnboardingCompletionStateType.InProgress &&
            userCompletionStep.QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.InProgress
        ) {
            if (
                completionStep.Quiz?.IsTimerEnabled &&
                isNullOrUndefined(userCompletionStep.QuizCompletion?.StartDateTime)
            ) {
                userCompletionStep.QuizCompletion.StartDateTime = new Date().toISOString();
            } else if (
                !completionStep.Quiz?.IsTimerEnabled &&
                !isNullOrUndefined(userCompletionStep.QuizCompletion?.StartDateTime)
            ) {
                userCompletionStep.QuizCompletion.StartDateTime = null;
            }
            this.updateUserCompletionStep(userCompletionStep);
        }

        return completionStep;
    }

    private openStepCompletionDialog(): void {
        const currentOpenedCompletionStep = this.currentOpenedCompletionStep();

        this.fiOnboardingCompletionHelperService
            .openStepCompletionDialog({
                onboardingId: this.onboardingId(),
                userId: this.userCompletion().UserId,
                step: currentOpenedCompletionStep.onboardingCompletionStep,
                userCompletionStep: this.userCompletionStepById().get(
                    currentOpenedCompletionStep.onboardingCompletionStep.OnboardingStepId,
                ),
                nextStepCompletionState: this.getNextStepCompletionState(
                    currentOpenedCompletionStep.onboardingCompletionStep,
                ),
            })
            .onYesButtonClick((userCompletionStep) => {
                this.updateUserCompletionStep(userCompletionStep);
                this.fiOnboardingCompletionSyncService.setCurrentOpenedCompletionStepData(undefined);
            });
    }

    private updateUserCompletionStep(userCompletionStep: IOnboardingCompletionUserStep): void {
        this.userCompletion.update((userCompletion) => ({
            ...userCompletion,
            Steps: userCompletion.Steps.map((step) =>
                step.OnboardingStepId === userCompletionStep.OnboardingStepId ? userCompletionStep : step,
            ),
        }));
    }

    private getNextStepCompletionState(step: IOnboardingCompletionStep): FiOnboardingCompletionStateType | undefined {
        const selectedUserCompletionStepIndex = this.getUserCompletionStepIndex(step);
        if (selectedUserCompletionStepIndex === this.userCompletion().Steps.length - 1) {
            return undefined;
        }
        return this.userCompletion().Steps[selectedUserCompletionStepIndex + 1].CompletionState;
    }

    private isStepCompleted(step: IOnboardingCompletionStep, index: number): boolean {
        return !!this.completedSteps()?.find(
            (completedStep) => completedStep.OnboardingStepId === step.OnboardingStepId,
        );
    }

    private isStepDisabled(step: IOnboardingCompletionStep, index: number): boolean {
        return !this.isActiveStep(step) && !this.isStepCompletedBeforeActive(step, index);
    }

    private isStepInProgress(step: IOnboardingCompletionStep, index: number): boolean {
        return this.fiOnboardingCompletionHelperService.isStepInProgress(
            this.userCompletionStepById().get(step.OnboardingStepId),
            step.Quiz?.AllowRetakes,
        );
    }

    private isStepCompletedBeforeActive(step: IOnboardingCompletionStep, index: number): boolean {
        const isStepCompleted = !!this.completedSteps()?.find(
            (completedStep) => completedStep.OnboardingStepId === step.OnboardingStepId,
        );
        if (!isStepCompleted) {
            return false;
        }
        const activeStepIndex = this.userActiveStepIndex();
        if (activeStepIndex === -1) {
            return true;
        }
        return index < activeStepIndex;
    }

    protected isSectionDisabled(section: IOnboardingCompletionSection, isActiveSection: boolean): boolean {
        return !this.completedSections()?.includes(section) && !isActiveSection;
    }

    private getTimerData(
        step: IOnboardingCompletionStep,
        index: number,
    ): IOnboardingQuizCompletionTimerData | undefined {
        const userCompletionStep = this.userCompletionStepById().get(step.OnboardingStepId);
        const startDateTime = userCompletionStep.QuizCompletion?.StartDateTime;
        if (
            isNullOrUndefined(startDateTime) ||
            userCompletionStep.QuizCompletion?.CompletionState !== FiOnboardingQuizCompletionStateType.InProgress
        ) {
            return undefined;
        }
        return this.fiOnboardingCompletionHelperService.getQuizTimerData(step.Quiz.TimerMs, startDateTime);
    }

    protected getUserCompletionStepIndex(step: IOnboardingCompletionStep): number {
        return this.userCompletion().Steps.findIndex(
            (userCompletionStep) => userCompletionStep.OnboardingStepId === step.OnboardingStepId,
        );
    }

    protected onCardClick(): void {
        if (this.expandOnCardClick() && !this.isExpanded()) {
            this.isExpanded.set(true);
        }
    }

    protected onTimerFinished(onboardingCompletionStep: IOnboardingCompletionStep) {
        if (
            this.fiOnboardingHelperService.isQuizStep(onboardingCompletionStep.ObjectType) &&
            this.currentOpenedCompletionStep()?.onboardingCompletionStep !== onboardingCompletionStep
        ) {
            const userCompletionStep: IOnboardingCompletionUserStep = cloneDeep(
                this.userCompletionStepById().get(onboardingCompletionStep.OnboardingStepId),
            );

            /*const quizCompletionState = this.fiOnboardingCompletionHelperService.getFinalQuizCompletionState(
                onboardingCompletionStep.Quiz,
                userCompletionStep,
            );
            const onboardingCompletionState = this.fiOnboardingCompletionHelperService.getStepCompletionStateQuiz(
                onboardingCompletionStep.Quiz.AllowRetakes,
                quizCompletionState,
            );*/

            //userCompletionStep.QuizCompletion.CompletionState = quizCompletionState;
            userCompletionStep.CompletionState = FiOnboardingCompletionStateType.Completed;
            const onboardingCompletionSaveModel: IOnboardingCompletionSaveModel =
                this.fiOnboardingCompletionHelperService.getOnboardingCompletionSaveModel({
                    onboardingId: this.onboardingId(),
                    userId: this.userCompletion().UserId,
                    onboardingStep: onboardingCompletionStep,
                    userCompletionStep: userCompletionStep,
                });

            this.fiOnboardingCompletionService
                .saveOnboardingCompletion(onboardingCompletionSaveModel)
                .then((result) => {
                    this.fiOnboardingCompletionHelperService.updateOnboardingCompletionFromSaveResult(
                        userCompletionStep,
                        result,
                    );
                    this.updateUserCompletionStep(userCompletionStep);
                });
        }
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiOnboardingCompletionStateType = FiOnboardingCompletionStateType;
}
